/**
 * MOCK DATA — synthetic trade ledger, decision artifacts and live tables.
 *
 * See `src/mock/banner.ts`. Nothing here is real. The values are generated from a
 * fixed seed and are internally consistent on purpose (net P&L = gross −
 * commission − swap − fees; R-multiple = net ÷ initial risk cash) so that the
 * dashboard's own arithmetic can be exercised against the §3.3 definitions
 * without pretending the numbers came from a broker.
 *
 * Generation strategy: build one coherent *decision* per completed trade, then
 * derive the trade row, the signal candidate, the feature snapshot, the research
 * assessment, the risk decision, the MT5 order request/result and the broker
 * deals from it. That is what makes the §3.2 B decision trail view meaningful
 * instead of a pile of unrelated fixtures.
 */
import type {
  BrokerDealView,
  BrokerOrderResultView,
  ClosedTradeRow,
  ConvictionTier,
  FeatureSnapshotView,
  HumanApprovalView,
  Mt5OrderRequestView,
  PositionRow,
  RegimeSnapshotView,
  ResearchAssessmentView,
  ResearchRecommendation,
  RiskDecisionView,
  RiskRuleResult,
  RiskVerdict,
  SchemaValidationStatus,
  Side,
  SignalBreakdownView,
  SignalCandidateView,
  SignalRow,
  StrategyMarkerView,
  SystemEventRow,
  WorkingOrderRow,
} from '../api/types';
import { createRng, mockId, mockUuid, pick, round, sessionDate } from './prng';

/* ------------------------------------------------------------------ *
 * Instruments
 * ------------------------------------------------------------------ */

export interface MockInstrument {
  symbol: string;
  internal_instrument_id: string;
  digits: number;
  point: number;
  pip: number;
  /** Account-currency value of one point for one lot. */
  value_per_point_per_lot: number;
  base_price: number;
  contract_size: number;
  volume_step: number;
  volume_min: number;
  spread_points: number;
}

export const MOCK_INSTRUMENTS: readonly MockInstrument[] = [
  { symbol: 'EURUSD', internal_instrument_id: 'inst-eurusd', digits: 5, point: 0.00001, pip: 0.0001, value_per_point_per_lot: 1.0, base_price: 1.08642, contract_size: 100_000, volume_step: 0.01, volume_min: 0.01, spread_points: 8 },
  { symbol: 'GBPUSD', internal_instrument_id: 'inst-gbpusd', digits: 5, point: 0.00001, pip: 0.0001, value_per_point_per_lot: 1.0, base_price: 1.27118, contract_size: 100_000, volume_step: 0.01, volume_min: 0.01, spread_points: 11 },
  { symbol: 'USDJPY', internal_instrument_id: 'inst-usdjpy', digits: 3, point: 0.001, pip: 0.01, value_per_point_per_lot: 0.67, base_price: 151.482, contract_size: 100_000, volume_step: 0.01, volume_min: 0.01, spread_points: 9 },
  { symbol: 'XAUUSD', internal_instrument_id: 'inst-xauusd', digits: 2, point: 0.01, pip: 0.1, value_per_point_per_lot: 1.0, base_price: 2_342.15, contract_size: 100, volume_step: 0.01, volume_min: 0.01, spread_points: 25 },
];

export const MOCK_STRATEGY_VERSIONS = ['orb-v1.0.0', 'vwap-reversion-v1.0.0', 'momentum-continuation-v1.0.0'] as const;

export const MOCK_REGIME_VERSIONS = 'regime-v1.2.0';
export const MOCK_FEATURE_VERSION = 'features-v1.4.0';

/** §3.3 `breakeven_threshold`: absorbs rounding noise so it cannot inflate win rate. */
export const BREAKEVEN_THRESHOLD = 0.5;

/* ------------------------------------------------------------------ *
 * Per-decision artifacts
 * ------------------------------------------------------------------ */

export interface DecisionArtifacts {
  signal: SignalCandidateView;
  features: FeatureSnapshotView;
  regime: RegimeSnapshotView;
  research: ResearchAssessmentView;
  risk: RiskDecisionView;
  approval: HumanApprovalView;
  request: Mt5OrderRequestView;
  results: BrokerOrderResultView[];
  deals: BrokerDealView[];
  breakdown: SignalBreakdownView;
}

export interface PendingOrderIntent {
  decision_id: string;
  order_intent_id: string;
  symbol: string;
  side: Side;
  lots: number;
  requested_price: number;
  stop_loss: number;
  take_profit: number;
  risk_verdict: RiskVerdict;
  quant_score: number;
  final_score: number;
  created_at: string;
  expires_at: string;
  strategy_version: string;
  research_confidence: number | null;
}

export interface MockDataset {
  now: number;
  session_date: string;
  account_currency: string;
  breakeven_threshold: number;
  instruments: readonly MockInstrument[];
  strategy_versions: readonly string[];
  closed_trades: ClosedTradeRow[];
  positions: PositionRow[];
  working_orders: WorkingOrderRow[];
  signals: SignalRow[];
  system_events: SystemEventRow[];
  markers: StrategyMarkerView[];
  decision_artifacts: Record<string, DecisionArtifacts>;
  live_regimes: RegimeSnapshotView[];
  pending_intents: PendingOrderIntent[];
}

/* ------------------------------------------------------------------ *
 * §7.2 risk rule order
 * ------------------------------------------------------------------ */

const RISK_RULE_TEMPLATE: readonly { rule_id: string; label: string; unit: string | null }[] = [
  { rule_id: 'halt_and_account_mode', label: 'Global halt and account-mode checks', unit: null },
  { rule_id: 'mt5_health_and_freshness', label: 'MT5 connection, reconciliation and data freshness', unit: 'ms' },
  { rule_id: 'instrument_session_allowlist', label: 'Instrument and session allow-list', unit: null },
  { rule_id: 'candidate_integrity', label: 'Candidate integrity and model/feature versions', unit: null },
  { rule_id: 'spread_volatility_event', label: 'Spread, volatility and event restrictions', unit: 'points' },
  { rule_id: 'concentration_correlation', label: 'Position concentration and correlation', unit: 'percent' },
  { rule_id: 'sizing_and_daily_loss', label: 'Per-trade and daily-loss sizing', unit: 'percent' },
  { rule_id: 'margin_order_check', label: 'Margin and broker order_check', unit: 'currency' },
  { rule_id: 'duplicate_idempotency', label: 'Duplicate-order and idempotency checks', unit: null },
  { rule_id: 'human_approval_gate', label: 'Human-approval gate', unit: null },
];

function buildRiskRules(verdict: RiskVerdict, spreadPoints: number, spreadLimit: number, rng: () => number): RiskRuleResult[] {
  // A rejected candidate fails exactly one rule, so the UI can show one plain
  // -language reason rather than a wall of red.
  const failureIndex = verdict === 'reject' ? 4 : verdict === 'defer' ? 4 : verdict === 'reduce' ? 6 : -1;
  return RISK_RULE_TEMPLATE.map((rule, index) => {
    const passed = index !== failureIndex;
    const isSpreadRule = rule.rule_id === 'spread_volatility_event';
    return {
      order: index + 1,
      rule_id: rule.rule_id,
      label: rule.label,
      passed,
      verdict: passed ? 'approve' : verdict,
      explanation: passed
        ? `${rule.label}: passed.`
        : isSpreadRule
          ? `Rejected: spread ${round(spreadPoints / 10, 1)} pips exceeds the configured ${round(spreadLimit / 10, 1)}-pip maximum.`
          : `Rejected: ${rule.label.toLowerCase()} did not pass for this candidate.`,
      observed_value: isSpreadRule ? spreadPoints : round(rng() * 10, 2),
      configured_limit: isSpreadRule ? spreadLimit : rule.unit === null ? null : round(1 + rng(), 2),
      unit: rule.unit,
    };
  });
}

/* ------------------------------------------------------------------ *
 * Research assessment generation
 * ------------------------------------------------------------------ */

const COVERAGE_CYCLE = ['sufficient', 'sufficient', 'partial', 'sufficient', 'stale', 'insufficient'] as const;
const RECOMMENDATION_CYCLE: readonly ResearchRecommendation[] = [
  'allow_quant_signal',
  'allow_quant_signal',
  'allow_reduced_size',
  'allow_quant_signal',
  'allow_reduced_size',
  'veto_quant_signal',
];
const CONVICTION_CYCLE: readonly ConvictionTier[] = ['low', 'medium', 'medium', 'high', 'low', 'medium'];

function buildResearch(
  index: number,
  decisionId: string,
  symbol: string,
  asOfMs: number,
  rng: () => number,
): ResearchAssessmentView {
  const coverage = pick(COVERAGE_CYCLE, index);
  const recommendation = pick(RECOMMENDATION_CYCLE, index);
  const confidence = round(0.35 + rng() * 0.55, 3);
  const researchScore = round(-0.4 + rng() * 1.3, 3);
  // §6.5 clamp: 0.10 × score × confidence, bounded to ±0.10.
  const rawAdjustment = 0.1 * researchScore * confidence;
  const researchAdjustment = round(Math.min(0.1, Math.max(-0.1, rawAdjustment)), 4);
  const eventPenalty = coverage === 'insufficient' ? -0.15 : 0;

  // Cycle through the validation outcomes so the UI exercises all of them,
  // including the §6.4 rule that a schema failure is logged, never repaired.
  const validationStatus: SchemaValidationStatus =
    index % 11 === 3 ? 'invalid' : index % 13 === 5 ? 'rejected_unknown_field' : index % 7 === 4 ? 'pending' : 'valid';
  const callStatus: ResearchAssessmentView['call']['status'] =
    validationStatus === 'invalid'
      ? 'schema_invalid'
      : validationStatus === 'rejected_unknown_field'
        ? 'malformed_json'
        : coverage === 'stale'
          ? 'timeout'
          : 'ok';

  const evidenceIds = [mockId('doc', index * 3 + 1), mockId('doc', index * 3 + 2)];
  const reasoningSteps = [
    { claim: 'Scheduled macro release within the assessment horizon was located in the supplied evidence package.', evidence_ids: [evidenceIds[0] ?? 'doc-0001'] },
    { claim: 'No counter-evidence contradicting the quantitative setup was found in the supplied documents.', evidence_ids: [evidenceIds[1] ?? 'doc-0002'] },
  ];

  return {
    assessment_id: mockUuid('ra', index),
    decision_id: decisionId,
    symbol,
    as_of: new Date(asOfMs).toISOString(),
    received_at: new Date(asOfMs + Math.round(400 + rng() * 2_200)).toISOString(),
    coverage_status: coverage,
    event_direction: pick(['neutral', 'bullish', 'bearish', 'mixed'] as const, index),
    event_relevance: round(rng(), 3),
    confidence,
    event_horizon_minutes: pick([30, 60, 120, 240], index),
    risk_flags:
      index % 3 === 0
        ? []
        : [
            {
              type: pick(['scheduled_macro_event', 'unscheduled_news', 'liquidity_hole', 'data_quality'] as const, index),
              severity: pick(['low', 'medium', 'high'] as const, index),
              evidence_ids: [evidenceIds[0] ?? 'doc-0001'],
              note: null,
            },
          ],
    evidence_ids: evidenceIds,
    counter_evidence_ids: index % 4 === 0 ? [mockId('doc', index * 3 + 9)] : [],
    research_score: researchScore,
    recommendation,
    rationale:
      'Bounded evidence-based summary generated for UI development. In production this text is the model rationale stored with the assessment and is rendered as untrusted content, never as instruction.',
    reasoning_steps: reasoningSteps,
    conviction_tier: pick(CONVICTION_CYCLE, index),
    proposed_stop_adjustment_pct: round((rng() - 0.5) * 0.16, 4),
    proposed_target_adjustment_pct: round((rng() - 0.5) * 0.2, 4),
    schema_validation: {
      status: validationStatus,
      schema_version: '2.0',
      errors:
        validationStatus === 'valid' || validationStatus === 'pending'
          ? []
          : [
              'evidence_ids[1] not present in the supplied evidence package',
              'proposed_stop_adjustment_pct outside the accepted range',
            ],
      raw_payload_ref: validationStatus === 'valid' ? null : `llm_raw/${mockId('payload', index)}.json`,
      validated_at: validationStatus === 'pending' ? null : new Date(asOfMs + 2_600).toISOString(),
    },
    prompt_version: 'prompt-research-2026.09-v2',
    model_version: 'deepseek-chat-2026-09',
    model_id: 'deepseek-chat',
    call: {
      llm_call_id: mockUuid('llm', index),
      started_at: new Date(asOfMs - 420).toISOString(),
      latency_ms: Math.round(600 + rng() * 3_400),
      prompt_tokens: Math.round(1_800 + rng() * 2_400),
      completion_tokens: Math.round(240 + rng() * 520),
      cost_usd: round(0.0007 + rng() * 0.004, 5),
      status: callStatus,
      attempt: callStatus === 'timeout' ? 2 : 1,
    },
    applied_adjustment: {
      research_adjustment: coverage === 'stale' || validationStatus === 'invalid' ? 0 : researchAdjustment,
      event_penalty: eventPenalty,
      clamp_min: -0.1,
      clamp_max: 0.1,
      clamped: Math.abs(rawAdjustment) > 0.1,
      conviction_multiplier: pick([0.5, 1.0, 1.25], index),
    },
    staleness_deadline_at: new Date(asOfMs + 3_600_000).toISOString(),
    expired: coverage === 'stale',
  };
}

/* ------------------------------------------------------------------ *
 * Build
 * ------------------------------------------------------------------ */

function buildDataset(): MockDataset {
  const now = Date.now();
  const rng = createRng(0x5eed_1234);
  const closedTrades: ClosedTradeRow[] = [];
  const markers: StrategyMarkerView[] = [];
  const decisionArtifacts: Record<string, DecisionArtifacts> = {};
  const signals: SignalRow[] = [];

  const TRADE_COUNT = 28;

  for (let index = 0; index < TRADE_COUNT; index += 1) {
    const instrument = pick(MOCK_INSTRUMENTS, index * 3 + Math.floor(index / 4));
    const strategy = pick(MOCK_STRATEGY_VERSIONS, index * 2 + (index % 5));
    const direction: Side = index % 3 === 2 ? 'sell' : 'buy';
    const dirSign = direction === 'buy' ? 1 : -1;
    const completedTradeId = mockId('trade', index + 1, 5);
    const decisionId = mockUuid('dec', index + 1);

    // Spread the ledger over roughly the last 21 hours, i.e. the current and the
    // previous session. That is a deliberately narrow window: it keeps every
    // row plausibly "recent" for a freshness-driven UI. The consequence is that
    // charts grouped by weekday only show two categories in mock mode.
    const minutesAgo = 12 + index * 47;
    const exitMs = now - minutesAgo * 60_000;
    const holdingMs = Math.round((14 + rng() * 190) * 60_000);
    const entryMs = exitMs - holdingMs;

    const entryPrice = round(instrument.base_price * (1 + (rng() - 0.5) * 0.004), instrument.digits);
    const stopDistance = Math.round(instrument.spread_points * (6 + rng() * 10));
    const targetDistance = Math.round(stopDistance * (1.1 + rng() * 1.1));
    const stopLoss = round(entryPrice - dirSign * stopDistance * instrument.point, instrument.digits);
    const takeProfit = round(entryPrice + dirSign * targetDistance * instrument.point, instrument.digits);

    // Signed points result drives both prices and P&L, so the row is coherent.
    const pointsResult = Math.round((rng() - 0.44) * targetDistance * 1.35);
    const exitPrice = round(entryPrice + dirSign * pointsResult * instrument.point, instrument.digits);
    const lots = round(0.05 + Math.floor(rng() * 6) * 0.01, 2);

    const gross = round(pointsResult * instrument.value_per_point_per_lot * lots, 2);
    const commission = round(lots * 7, 2);
    const swap = round((rng() - 0.55) * lots * 3, 2);
    const fees = round(0.35 + rng() * 0.4, 2);
    const net = round(gross - commission - swap - fees, 2);
    const outcome = net > BREAKEVEN_THRESHOLD ? 'win' : net < -BREAKEVEN_THRESHOLD ? 'loss' : 'breakeven';

    const initialRiskCash = round((stopDistance * instrument.value_per_point_per_lot * lots), 2);
    const rMultiple = initialRiskCash === 0 ? null : round(net / initialRiskCash, 2);
    const exitReason =
      outcome === 'win' ? pick(['target', 'signal', 'time'] as const, index)
      : outcome === 'loss' ? pick(['stop', 'risk', 'time'] as const, index)
      : pick(['time', 'signal', 'session_flatten'] as const, index);

    const regime: RegimeSnapshotView = {
      regime_version: MOCK_REGIME_VERSIONS,
      symbol: instrument.symbol,
      bar_time: new Date(entryMs - (entryMs % 300_000)).toISOString(),
      trend_strength: pick(['trending', 'ranging'] as const, index),
      volatility_bucket: pick(['low', 'normal', 'high'] as const, index * 2),
      session_phase: pick(['opening', 'mid_session', 'pre_close'] as const, Math.floor(index / 3)),
      minutes_since_open: 15 + Math.floor(rng() * 300),
      inputs: { adx: round(14 + rng() * 22, 2), realized_vol_percentile: round(rng(), 3) },
      features_hash: `sha256:${mockId('regime', index, 8)}`,
    };

    const features: FeatureSnapshotView = {
      feature_snapshot_id: mockId('feat', index + 1, 5),
      feature_version: MOCK_FEATURE_VERSION,
      symbol: instrument.symbol,
      computed_at: new Date(entryMs - 1_200).toISOString(),
      completed_bar_time: new Date(entryMs - (entryMs % 300_000)).toISOString(),
      values: [
        { name: 'opening_range_breakout_atr', value: round(rng() * 1.6, 3), kind: 'number', unit: 'ATR', contribution: round(rng() * 0.22, 4) },
        { name: 'vwap_deviation_sigma', value: round((rng() - 0.5) * 4, 3), kind: 'number', unit: 'sigma', contribution: round((rng() - 0.5) * 0.18, 4) },
        { name: 'atr14', value: round(instrument.base_price * 0.0008 * (0.6 + rng()), instrument.digits), kind: 'number', unit: 'price', contribution: null },
        { name: 'relative_volume', value: round(0.7 + rng() * 1.4, 3), kind: 'number', unit: 'ratio', contribution: round(rng() * 0.12, 4) },
        { name: 'spread_points', value: instrument.spread_points, kind: 'number', unit: 'points', contribution: null },
        { name: 'session_phase', value: regime.session_phase, kind: 'enum', unit: null, contribution: round(rng() * 0.06, 4) },
        { name: 'minutes_since_open', value: regime.minutes_since_open, kind: 'number', unit: 'minutes', contribution: null },
        { name: 'is_completed_bar', value: true, kind: 'bool', unit: null, contribution: null },
        { name: 'news_blackout_active', value: index % 5 === 0, kind: 'bool', unit: null, contribution: round(-0.05, 4) },
      ],
      features_hash: `sha256:${mockId('features', index, 8)}`,
    };

    const quantScore = round(0.42 + rng() * 0.5, 4);
    const modelConfidence = round(0.35 + rng() * 0.55, 4);
    const research = buildResearch(index, decisionId, instrument.symbol, entryMs, rng);
    const finalScore = round(
      quantScore + research.applied_adjustment.research_adjustment + research.applied_adjustment.event_penalty,
      4,
    );
    const spreadLimit = instrument.spread_points * 2;

    const riskDecision = {
      risk_decision_id: mockUuid('rd', index),
      decision_id: decisionId,
      decided_at: new Date(entryMs - 600).toISOString(),
      verdict: 'approve' as RiskVerdict,
      risk_config_hash: 'sha256:risk-cfg-9f2c41ab',
      risk_config_version: 'risk-2026.09.4',
      rules: buildRiskRules('approve', instrument.spread_points, spreadLimit, rng),
      approved_lots: lots,
      approved_entry: entryPrice,
      approved_stop_loss: stopLoss,
      approved_take_profit: takeProfit,
      sizing: {
        risk_cash: round(initialRiskCash * 1.0, 2),
        risk_per_lot: round(initialRiskCash / lots, 2),
        raw_lots: round(lots * 1.04, 3),
        volume_step: instrument.volume_step,
        approved_lots: lots,
        conviction_multiplier: research.applied_adjustment.conviction_multiplier,
        strategy_allocation_share: 0.4,
      },
    } satisfies RiskDecisionView;

    // Every fourth trade ran without a human click, under §19.4 constrained_auto.
    const autoApproved = index % 4 === 1;
    const approval: HumanApprovalView = {
      approval_id: mockUuid('ap', index),
      decision_id: decisionId,
      operator: autoApproved ? null : index % 7 === 3 ? 'operator.jhart' : 'operator.ayoub',
      decision: autoApproved ? 'auto_approved' : 'approved',
      decided_at: autoApproved ? null : new Date(entryMs - 420).toISOString(),
      reason: autoApproved ? 'constrained_auto execution mode: risk verdict forwarded without a manual click (§19.4).' : null,
      expires_at: new Date(entryMs + 120_000).toISOString(),
      execution_mode_at_decision: autoApproved ? 'demo_enabled' : 'approval_required',
    };

    const orderIntentId = mockUuid('oi', index);
    const submittedMs = entryMs + Math.round(180 + rng() * 400);
    const request: Mt5OrderRequestView = {
      order_intent_id: orderIntentId,
      decision_id: decisionId,
      idempotency_key: mockUuid('idem', index),
      submitted_at: new Date(submittedMs).toISOString(),
      request: {
        symbol: instrument.symbol,
        side: direction,
        lots,
        order_type: 'ORDER_TYPE_BUY_LIMIT',
        price: entryPrice,
        stop_loss: stopLoss,
        take_profit: takeProfit,
        deviation_points: 12,
        magic_number: 55_012_001,
        comment: `intraday-ai:${mockId('oi', index, 6)}`,
      },
      ea_acknowledged_at: new Date(submittedMs + 40 + rng() * 90).toISOString(),
      order_check: {
        performed_at: new Date(submittedMs - 30).toISOString(),
        retcode: 0,
        retcode_label: 'TRADE_RETCODE_DONE',
        margin_required: round(lots * 1_080, 2),
        free_margin_after: round(9_400 - lots * 1_080, 2),
        advisory_only: true,
      },
    };

    const slippage = Math.round((rng() - 0.45) * 6);
    const result: BrokerOrderResultView = {
      broker_order_id: mockUuid('bo', index),
      order_intent_id: orderIntentId,
      order_ticket: String(412_000_000 + index * 37),
      submitted_at: new Date(submittedMs).toISOString(),
      final_state: index % 9 === 4 ? 'partially_filled' : 'placed',
      retcode: 10009,
      retcode_label: 'TRADE_RETCODE_DONE',
      retcode_is_retryable: false,
      requested_price: entryPrice,
      fill_price: round(entryPrice + dirSign * slippage * instrument.point, instrument.digits),
      filled_lots: index % 9 === 4 ? round(lots / 2, 2) : lots,
      fill_latency_ms: Math.round(60 + rng() * 260),
      estimated_slippage_points: Math.round((rng() - 0.4) * 3),
      actual_slippage_points: slippage,
      rejection_reason: null,
    };

    const deals: BrokerDealView[] = [
      {
        deal_ticket: String(512_000_000 + index * 53),
        broker_order_id: result.broker_order_id,
        position_ticket: String(312_000_000 + index * 11),
        executed_at: new Date(submittedMs + 120).toISOString(),
        symbol: instrument.symbol,
        side: direction,
        entry: 'in',
        volume: result.filled_lots,
        price: result.fill_price ?? entryPrice,
        commission: round(-commission / 2, 2),
        swap: 0,
        fee: 0,
        profit: 0,
        comment: 'entry',
        magic_number: 55_012_001,
      },
      {
        deal_ticket: String(512_000_001 + index * 53),
        broker_order_id: null,
        position_ticket: String(312_000_000 + index * 11),
        executed_at: new Date(exitMs).toISOString(),
        symbol: instrument.symbol,
        side: direction === 'buy' ? 'sell' : 'buy',
        entry: 'out',
        volume: result.filled_lots,
        price: exitPrice,
        commission: round(-commission / 2, 2),
        swap,
        fee: fees,
        profit: gross,
        comment: exitReason === 'stop' ? 'sl' : exitReason === 'target' ? 'tp' : 'close',
        magic_number: 55_012_001,
      },
    ];

    const signal: SignalCandidateView = {
      decision_id: decisionId,
      strategy_version: strategy,
      symbol: instrument.symbol,
      decision_time: new Date(entryMs - 900).toISOString(),
      side: direction,
      entry_type: 'marketable_limit',
      proposed_entry: entryPrice,
      stop_loss: stopLoss,
      take_profit: takeProfit,
      time_exit_at: new Date(entryMs + 3 * 3_600_000).toISOString(),
      quant_score: quantScore,
      model_confidence: modelConfidence,
      features_hash: features.features_hash,
      status: 'executed',
      arbitration: {
        outcome: index % 6 === 2 ? 'superseded' : 'selected',
        superseded_by: index % 6 === 2 ? mockUuid('dec', index + 100) : null,
        note: index % 6 === 2 ? 'Same symbol and direction from a second strategy; the higher quant score was kept (§18.4).' : null,
      },
    };

    const breakdown: SignalBreakdownView = {
      decision_id: decisionId,
      symbol: instrument.symbol,
      as_of: new Date(entryMs - 500).toISOString(),
      base_score: quantScore,
      research_adjustment: research.applied_adjustment.research_adjustment,
      event_penalty: research.applied_adjustment.event_penalty,
      final_score: finalScore,
      decision: finalScore >= 0.5 ? 'trade' : 'no_trade',
      decision_reason:
        finalScore >= 0.5
          ? `Final score ${finalScore} is at or above the configured entry threshold of 0.50.`
          : `Final score ${finalScore} is below the configured entry threshold of 0.50.`,
      components: [
        {
          kind: 'quantitative',
          label: `Quantitative ${strategy} score`,
          contribution: quantScore,
          detail: 'Deterministic strategy output from the closed-bar feature snapshot. This is the only component that can originate a trade.',
          is_ai_derived: false,
          clamped_to: null,
        },
        {
          kind: 'research_adjustment',
          label: 'Bounded AI research adjustment',
          contribution: research.applied_adjustment.research_adjustment,
          detail: 'clamp(0.10 × research_score × confidence, −0.10, +0.10) — a bounded adjustment to the quantitative score, not a signal in its own right.',
          is_ai_derived: true,
          clamped_to: research.applied_adjustment.clamped ? 0.1 : null,
        },
        {
          kind: 'event_penalty',
          label: 'Event risk penalty',
          contribution: research.applied_adjustment.event_penalty,
          detail:
            research.applied_adjustment.event_penalty === 0
              ? 'No medium or high event risk flagged in the research assessment.'
              : '−0.15 applied for a medium-severity flagged event.',
          is_ai_derived: true,
          clamped_to: null,
        },
        {
          kind: 'risk_constraint',
          label: 'Risk constraints applied downstream',
          contribution: 0,
          detail: `Per-trade risk 0.42% of demo equity is within the 0.50% cap; ${riskDecision.rules.length} risk rules were evaluated and all passed.`,
          is_ai_derived: false,
          clamped_to: null,
        },
      ],
      risk_constraints: [
        { constraint_id: 'per_trade_risk', label: 'Per-trade risk', status: 'satisfied', explanation: 'Position sized to 0.42% of demo equity against a 0.50% cap.', observed_value: 0.42, limit_value: 0.5, unit: 'percent_equity' },
        { constraint_id: 'daily_loss', label: 'Daily loss budget', status: 'satisfied', explanation: 'Remaining daily loss budget after this candidate: 0.71% of equity.', observed_value: 1.29, limit_value: 2.0, unit: 'percent_equity' },
        { constraint_id: 'max_open_positions', label: 'Open positions', status: 'satisfied', explanation: '2 of 3 permitted positions open at decision time.', observed_value: 2, limit_value: 3, unit: 'count' },
        { constraint_id: 'spread', label: 'Maximum spread', status: 'satisfied', explanation: `Observed spread ${round(instrument.spread_points / 10, 1)} pips against a ${round(spreadLimit / 10, 1)}-pip cap.`, observed_value: instrument.spread_points, limit_value: spreadLimit, unit: 'points' },
      ],
      thresholds: { enter_above: 0.5, reduce_below: 0.35, configured_by: 'strategy-config:orb-v1.0.0' },
      research_can_originate_trade: false,
      ai_output_label: 'research adjustment',
    };

    decisionArtifacts[completedTradeId] = {
      signal,
      features,
      regime,
      research,
      risk: riskDecision,
      approval,
      request,
      results: [result],
      deals,
      breakdown,
    };

    closedTrades.push({
      completed_trade_id: completedTradeId,
      decision_id: decisionId,
      strategy_version: strategy,
      symbol: instrument.symbol,
      internal_instrument_id: instrument.internal_instrument_id,
      direction,
      entry_timestamp: new Date(entryMs).toISOString(),
      average_entry_price: entryPrice,
      entered_volume: lots,
      exit_timestamp: new Date(exitMs).toISOString(),
      average_exit_price: exitPrice,
      exited_volume: lots,
      gross_realized_pnl: gross,
      commission,
      swap,
      fees,
      net_realized_pnl: net,
      initial_risk_cash: initialRiskCash,
      r_multiple: rMultiple,
      max_favorable_excursion: round(Math.max(0, pointsResult) + rng() * 40, 2),
      max_adverse_excursion: round(-(Math.min(0, pointsResult) + rng() * 30), 2),
      exit_reason: exitReason,
      outcome,
      holding_time_ms: holdingMs,
      order_ticket: result.order_ticket,
      position_ticket: deals[0]?.position_ticket ?? null,
      deal_tickets: deals.map((deal) => deal.deal_ticket),
      magic_number: 55_012_001,
      research_confidence: research.confidence,
      regime: {
        trend_strength: regime.trend_strength,
        volatility_bucket: regime.volatility_bucket,
        session_phase: regime.session_phase,
        regime_version: regime.regime_version,
      },
      tags:
        outcome === 'win'
          ? ['clean-fill', 'target-hit']
          : outcome === 'loss'
            ? ['stop-hit', index % 3 === 0 ? 'news-window' : 'normal']
            : ['flat-exit'],
    });

    markers.push({
      marker_id: mockId('marker', index + 1, 5),
      decision_id: decisionId,
      symbol: instrument.symbol,
      signal_time: signal.decision_time,
      side: direction,
      proposed_entry: entryPrice,
      stop_loss: stopLoss,
      take_profit: takeProfit,
      actual_fill_price: result.fill_price,
      actual_fill_time: request.ea_acknowledged_at,
      outcome,
      label: `${strategy} ${direction} ${lots} lots`,
    });
  }

  /* ---------------- open positions (§3.2 A) ---------------- */

  const positions: PositionRow[] = MOCK_INSTRUMENTS.slice(0, 3).map((instrument, index) => {
    const direction: Side = index === 1 ? 'sell' : 'buy';
    const dirSign = direction === 'buy' ? 1 : -1;
    const entryPrice = round(instrument.base_price * (1 - dirSign * 0.0006), instrument.digits);
    const latestBid = instrument.base_price;
    const latestAsk = round(instrument.base_price + instrument.spread_points * instrument.point, instrument.digits);
    const lots = round(0.08 + index * 0.03, 2);
    const pointsOpen = Math.round(dirSign * (latestBid - entryPrice) / instrument.point);
    const unrealized = round(pointsOpen * instrument.value_per_point_per_lot * lots, 2);
    const openedMs = now - (18 + index * 47) * 60_000;
    return {
      position_ticket: String(312_000_000 + index * 11),
      symbol: instrument.symbol,
      internal_instrument_id: instrument.internal_instrument_id,
      side: direction,
      lots,
      entry_price: entryPrice,
      latest_bid: latestBid,
      latest_ask: latestAsk,
      quote_age_ms: 420 + index * 260,
      spread_points: instrument.spread_points,
      unrealized_pnl: unrealized,
      notional: round(lots * instrument.contract_size * latestBid, 2),
      stop_loss: round(entryPrice - dirSign * instrument.spread_points * 8 * instrument.point, instrument.digits),
      take_profit: round(entryPrice + dirSign * instrument.spread_points * 14 * instrument.point, instrument.digits),
      // Deliberately one position without a broker-confirmed stop, so the
      // §16.2.4 invariant is visible on screen rather than assumed.
      stop_confirmed_at_broker: index !== 2,
      opened_at: new Date(openedMs).toISOString(),
      age_ms: now - openedMs,
      strategy_version: pick(MOCK_STRATEGY_VERSIONS, index),
      decision_id: mockUuid('dec', 900 + index),
      risk_status: index === 2 ? 'unprotected_stop' : 'protected',
      magic_number: 55_012_001,
    };
  });

  /* ---------------- working orders (§3.2 A) ---------------- */

  const workingOrders: WorkingOrderRow[] = MOCK_INSTRUMENTS.slice(1, 4).map((instrument, index) => {
    const placedMs = now - (6 + index * 9) * 60_000;
    const state: WorkingOrderRow['order_state'] = index === 1 ? 'partially_filled' : index === 2 ? 'pending_cancel' : 'placed';
    return {
      order_ticket: String(412_900_000 + index * 17),
      order_intent_id: mockUuid('oi', 700 + index),
      symbol: instrument.symbol,
      side: index === 1 ? 'sell' : 'buy',
      lots: round(0.05 + index * 0.02, 2),
      requested_price: round(instrument.base_price * (index === 1 ? 1.0012 : 0.9988), instrument.digits),
      order_state: state,
      placed_at: new Date(placedMs).toISOString(),
      age_ms: now - placedMs,
      deviation_points: 12,
      filled_lots: state === 'partially_filled' ? 0.04 : 0,
      cancellable: state !== 'pending_cancel' && state !== 'partially_filled',
      cancel_blocked_reason:
        state === 'partially_filled'
          ? 'Partially filled orders cannot be cancelled at the broker; cancel the remaining volume from the terminal or wait for the fill to complete.'
          : state === 'pending_cancel'
            ? 'A cancellation request is already in flight.'
            : null,
      magic_number: 55_012_001,
    };
  });

  /* ---------------- live signals (§3.2 A) ---------------- */

  const liveSignalStates: SignalRow['status'][] = ['awaiting_approval', 'awaiting_approval', 'awaiting_research', 'awaiting_risk', 'candidate', 'rejected'];
  const pendingIntents: PendingOrderIntent[] = [];

  for (let index = 0; index < liveSignalStates.length; index += 1) {
    const instrument = pick(MOCK_INSTRUMENTS, index + 1);
    const direction: Side = index % 2 === 0 ? 'buy' : 'sell';
    const dirSign = direction === 'buy' ? 1 : -1;
    const status = pick(liveSignalStates, index);
    const decisionId = mockUuid('dec', 500 + index);
    const createdMs = now - (2 + index * 4) * 60_000;
    const entryPrice = round(instrument.base_price + dirSign * instrument.spread_points * instrument.point, instrument.digits);
    const stopLoss = round(entryPrice - dirSign * instrument.spread_points * 9 * instrument.point, instrument.digits);
    const takeProfit = round(entryPrice + dirSign * instrument.spread_points * 16 * instrument.point, instrument.digits);
    const quantScore = round(0.46 + index * 0.035, 4);
    const finalScore = round(quantScore + (index % 2 === 0 ? 0.04 : -0.03), 4);
    const verdict: RiskVerdict | null =
      status === 'rejected' ? 'reject' : status === 'awaiting_approval' ? 'approve' : status === 'awaiting_risk' ? null : 'approve';

    signals.push({
      decision_id: decisionId,
      symbol: instrument.symbol,
      strategy_version: pick(MOCK_STRATEGY_VERSIONS, index + 1),
      decision_time: new Date(createdMs).toISOString(),
      side: direction,
      entry_type: index % 3 === 0 ? 'marketable_limit' : 'limit',
      proposed_entry: entryPrice,
      stop_loss: stopLoss,
      take_profit: takeProfit,
      time_exit_at: new Date(createdMs + 3 * 3_600_000).toISOString(),
      quant_score: quantScore,
      model_confidence: round(0.4 + index * 0.06, 4),
      final_score: verdict === null ? null : finalScore,
      research_status: status === 'awaiting_research' ? 'pending' : index === 5 ? 'stale' : 'valid',
      risk_verdict: verdict,
      rejection_reason:
        status === 'rejected'
          ? `Rejected: ${instrument.symbol} spread ${round(instrument.spread_points / 10 + 0.9, 1)} pips exceeds configured ${round(instrument.spread_points / 10, 1)}-pip maximum.`
          : null,
      status,
      regime: {
        trend_strength: index % 2 === 0 ? 'trending' : 'ranging',
        volatility_bucket: index === 2 ? 'high' : 'normal',
        session_phase: 'mid_session',
        regime_version: MOCK_REGIME_VERSIONS,
      },
      age_ms: now - createdMs,
    });

    if (status === 'awaiting_approval') {
      pendingIntents.push({
        decision_id: decisionId,
        order_intent_id: mockUuid('oi', 500 + index),
        symbol: instrument.symbol,
        side: direction,
        lots: round(0.06 + index * 0.02, 2),
        requested_price: entryPrice,
        stop_loss: stopLoss,
        take_profit: takeProfit,
        risk_verdict: 'approve',
        quant_score: quantScore,
        final_score: finalScore,
        created_at: new Date(createdMs).toISOString(),
        expires_at: new Date(createdMs + 120_000).toISOString(),
        strategy_version: pick(MOCK_STRATEGY_VERSIONS, index + 1),
        research_confidence: round(0.5 + index * 0.1, 3),
      });
    }
  }

  /* ---------------- system events (§3.2 A) ---------------- */

  const eventTemplates: { kind: SystemEventRow['kind']; severity: SystemEventRow['severity']; summary: string; detail: string }[] = [
    { kind: 'mt5_connection', severity: 'info', summary: 'MT5 EA bridge authenticated', detail: 'IntradayAiBridge.mq5 v1.0.0 completed the version-1 handshake over loopback TCP.' },
    { kind: 'reconciliation', severity: 'info', summary: 'Reconciliation completed with no mismatches', detail: '3 positions, 3 working orders and 28 closed trades matched the EA snapshot.' },
    { kind: 'data_quality_gate', severity: 'warning', summary: 'Tick gap detected on GBPUSD', detail: 'A 4.2-second gap exceeded the 2-second continuity threshold during the London open. Bars were rebuilt from the next clean tick.' },
    { kind: 'risk_event', severity: 'warning', summary: 'Daily loss warning threshold reached', detail: 'Realized + unrealized loss reached 65% of the configured daily cap. New entries still require explicit approval.' },
    { kind: 'manual_action', severity: 'info', summary: 'Operator changed max spread for EURUSD', detail: 'Prior 15 points, new 12 points. Reason: "London open spreads widened beyond the strategy assumption."' },
    { kind: 'execution', severity: 'info', summary: 'Order intent submitted to the EA', detail: 'EURUSD buy 0.08 lots, marketable limit, deviation 12 points, magic 55012001.' },
    { kind: 'api_error', severity: 'error', summary: 'DeepSeek call returned schema-invalid JSON', detail: 'Assessment rejected and logged with its raw payload; a neutral research adjustment (0.00) was applied. No retry was issued inside the decision window.' },
    { kind: 'scheduler', severity: 'info', summary: 'Flatten deadline registered', detail: 'End-of-session flatten scheduled 15 minutes before the broker close at 21:00 broker time.' },
    { kind: 'research', severity: 'warning', summary: 'Research assessment expired before use', detail: 'A pre-session assessment for XAUUSD exceeded its staleness deadline and was treated as neutral.' },
    { kind: 'config_change', severity: 'info', summary: 'Risk configuration versioned', detail: 'risk-2026.09.4 written to the append-only audit ledger with the operator, prior value, new value and reason.' },
  ];

  const systemEvents: SystemEventRow[] = Array.from({ length: 24 }, (_, index) => {
    const template = pick(eventTemplates, index);
    const occurredMs = now - (3 + index * 23) * 60_000;
    return {
      event_id: mockUuid('ev', index + 1),
      occurred_at: new Date(occurredMs).toISOString(),
      kind: template.kind,
      severity: template.severity,
      summary: template.summary,
      detail: template.detail,
      actor: template.kind === 'manual_action' || template.kind === 'config_change' ? 'operator.ayoub' : 'system:reconciliation_engine',
      correlation_id: mockUuid('corr', index % 5),
      symbol: template.summary.includes('EURUSD') ? 'EURUSD' : template.summary.includes('GBPUSD') ? 'GBPUSD' : template.summary.includes('XAUUSD') ? 'XAUUSD' : null,
      redacted: true,
    };
  }).sort((left, right) => right.occurred_at.localeCompare(left.occurred_at));

  /* ---------------- live regime snapshots (§18.2) ---------------- */

  const liveRegimes: RegimeSnapshotView[] = MOCK_INSTRUMENTS.map((instrument, index) => ({
    regime_version: MOCK_REGIME_VERSIONS,
    symbol: instrument.symbol,
    bar_time: new Date(now - (now % 300_000)).toISOString(),
    trend_strength: pick(['trending', 'ranging'] as const, index),
    volatility_bucket: pick(['low', 'normal', 'high'] as const, index + 1),
    session_phase: index === 3 ? 'pre_close' : 'mid_session',
    minutes_since_open: 184 + index * 3,
    inputs: { adx: round(16 + index * 3.4, 2), realized_vol_percentile: round(0.2 + index * 0.17, 3) },
    features_hash: `sha256:${mockId('regime-live', index, 8)}`,
  }));

  return {
    now,
    session_date: sessionDate(now),
    account_currency: 'USD',
    breakeven_threshold: BREAKEVEN_THRESHOLD,
    instruments: MOCK_INSTRUMENTS,
    strategy_versions: MOCK_STRATEGY_VERSIONS,
    closed_trades: closedTrades,
    positions,
    working_orders: workingOrders,
    signals,
    system_events: systemEvents,
    markers,
    decision_artifacts: decisionArtifacts,
    live_regimes: liveRegimes,
    pending_intents: pendingIntents,
  };
}

/** Module-level singleton so a re-render never reshuffles the fixtures. */
export const MOCK_DATASET: MockDataset = buildDataset();
